/**
 * Public Trust & Security page — reachable without a session at /security.
 *
 * Built to do one job: give a prospect or compliance reviewer every
 * answer they'd ask in a security questionnaire, on a single page, in
 * plain language. Static content — no backend, no auth. Bilingual
 * (English + Brazilian Portuguese) to match Privacy and Terms.
 *
 * The per-user MFA settings screen moved to `/account/security` so the
 * obvious `/security` URL belongs to this public page. `/trust` is kept
 * as an alias for old marketing links.
 */
import { useState } from "react";
import { LegalLayout } from "./LegalLayout";
import {
  ShieldCheck,
  Lock,
  Server,
  Users,
  FileCheck2,
  Sparkles,
  Mail,
  ScrollText,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

type Lang = "en" | "pt";

const EFFECTIVE_DATE = "April 20, 2026";
const EFFECTIVE_DATE_PT = "20 de abril de 2026";

interface BulletSection {
  icon: LucideIcon;
  title: string;
  blurb?: string;
  bullets: string[];
}

// ── Copy ───────────────────────────────────────────────────────────────

const EN = {
  title: "Security at Lenavio",
  trustCenter: "Trust Center",
  hero: "Your data security is our top priority.",
  heroBlurb:
    "Lenavio is the system of record for your people. We treat that responsibility the way your security team would — with defence in depth, transparent controls, and no shortcuts around privacy.",
  complianceTitle: "Compliance",
  complianceBlurb:
    "Lenavio is built from day one to align with modern privacy and security regimes. If your team needs the paperwork, reach out — we will send the most recent DPA and security questionnaire on request.",
  contactsTitle: "Reporting & contacts",
  reportVulnTitle: "Report a vulnerability",
  reportVulnBody: (email: ReactNode) => (
    <>
      Email {email} with a description and reproduction steps. We respond to
      every report within two business days.
    </>
  ),
  dpaTitle: "Data Processing Addendum",
  dpaBody: (email: ReactNode) => (
    <>Request our current DPA by emailing {email} from a verified company address.</>
  ),
  readyBadge: "Ready",
  roadmapBadge: "Roadmap",
  langToggleEn: "English",
  langTogglePt: "Português (Brasil)",
  sibling: { to: "/privacy", label: "Read the Privacy Policy →" },
  sections: [
    {
      icon: Server,
      title: "Infrastructure",
      bullets: [
        "Hosted on SOC 2–compliant cloud infrastructure with isolated production environments.",
        "All data encrypted in transit with TLS 1.3 and at rest with AES-256.",
        "Automated database backups every 24 hours with 30-day retention and point-in-time recovery.",
        "Target 99.9% uptime with redundant services and health-checked deploys.",
      ],
    },
    {
      icon: Lock,
      title: "Application security",
      bullets: [
        "Role-based access control across four permission levels: Employee, Manager, HR Admin, Super Admin.",
        "Every request authenticated with short-lived JWT access tokens and automatic refresh rotation.",
        "CSRF protection on all state-changing endpoints plus strict CORS and Content-Security-Policy headers.",
        "Aggressive rate limiting on authentication and password-reset endpoints to thwart brute-force attempts.",
        "Passwords hashed with bcrypt (12 rounds) — the plaintext never leaves your browser memory.",
        "Full, tamper-evident audit log of user actions, viewable by Super Admins and exportable as CSV.",
        "Optional single sign-on via Google, Microsoft, or any SAML 2.0 identity provider, with just-in-time provisioning.",
        "Multi-factor authentication available to every user from their personal security settings.",
      ],
    },
    {
      icon: FileCheck2,
      title: "Data privacy",
      bullets: [
        "Every employee can export a complete copy of their personal data at any time.",
        "Data deletion requests are processed within 30 days, with a scheduled grace window so nothing vanishes by accident.",
        "Lenavio never sells employee data to third parties — full stop.",
        "Tenant data is strictly scoped: every query is filtered by organization, enforced at the ORM layer, not by convention.",
        "Regional data residency available on enterprise plans.",
      ],
    },
    {
      icon: Sparkles,
      title: "Responsible AI",
      bullets: [
        "Our AI agents (Atlas, Sentinel, Pilot) run on Anthropic's Claude API with zero-retention data handling.",
        "No customer or employee data is ever used to train foundation models.",
        "AI-generated content is surfaced as suggestions — a human reviews and approves before it takes effect.",
        "Every AI decision is logged with the prompt, the model response, and the human who acted on it.",
        "Employees can opt out of AI-assisted workflows from their profile settings.",
      ],
    },
  ] satisfies BulletSection[],
  compliance: [
    {
      label: "LGPD",
      status: "ready" as const,
      blurb:
        "Brazil's general data-protection law: controls for access, correction, portability, and deletion of personal data.",
    },
    {
      label: "GDPR",
      status: "ready" as const,
      blurb:
        "EU data-protection regulation: lawful basis, data-subject rights, breach notification, and DPA on request.",
    },
    {
      label: "CCPA / CPRA",
      status: "ready" as const,
      blurb:
        "California privacy rights: the right to know, delete, correct, and opt out of the sale or sharing of personal information.",
    },
    {
      label: "SOC 2 Type II",
      status: "roadmap" as const,
      blurb:
        "Audit engagement planned for late 2026. Security controls already map to the Trust Services Criteria.",
    },
  ],
};

const PT: typeof EN = {
  title: "Segurança na Lenavio",
  trustCenter: "Central de Confiança",
  hero: "A segurança dos seus dados é nossa prioridade máxima.",
  heroBlurb:
    "A Lenavio é o sistema de registro da sua equipe. Tratamos essa responsabilidade como seu próprio time de segurança trataria — com defesa em profundidade, controles transparentes e sem atalhos quando o assunto é privacidade.",
  complianceTitle: "Conformidade",
  complianceBlurb:
    "A Lenavio foi construída desde o primeiro dia para se alinhar aos regimes modernos de privacidade e segurança. Se seu time precisar da documentação, fale com a gente — enviamos o DPA mais recente e o questionário de segurança sob demanda.",
  contactsTitle: "Reportes e contatos",
  reportVulnTitle: "Reportar uma vulnerabilidade",
  reportVulnBody: (email: ReactNode) => (
    <>
      Envie um e-mail para {email} com a descrição e os passos para reproduzir.
      Respondemos a cada relato em até dois dias úteis.
    </>
  ),
  dpaTitle: "Acordo de Processamento de Dados (DPA)",
  dpaBody: (email: ReactNode) => (
    <>
      Solicite nosso DPA atual enviando um e-mail para {email} a partir de um
      endereço corporativo verificado.
    </>
  ),
  readyBadge: "Pronto",
  roadmapBadge: "Roadmap",
  langToggleEn: "English",
  langTogglePt: "Português (Brasil)",
  sibling: { to: "/privacy", label: "Ler a Política de Privacidade →" },
  sections: [
    {
      icon: Server,
      title: "Infraestrutura",
      bullets: [
        "Hospedada em infraestrutura em nuvem com conformidade SOC 2 e ambientes de produção isolados.",
        "Todos os dados trafegam criptografados com TLS 1.3 e são armazenados com AES-256.",
        "Backups automáticos a cada 24 horas, com retenção de 30 dias e recuperação pontual (PITR).",
        "Meta de 99,9% de disponibilidade, com serviços redundantes e deploys com health-check.",
      ],
    },
    {
      icon: Lock,
      title: "Segurança da aplicação",
      bullets: [
        "Controle de acesso baseado em papéis, em quatro níveis: Funcionário, Gestor, Admin de RH e Super Admin.",
        "Cada requisição é autenticada com tokens JWT de curta duração e rotação automática de refresh.",
        "Proteção CSRF em todos os endpoints de escrita, além de CORS restrito e cabeçalhos de Content-Security-Policy.",
        "Rate limiting agressivo nos endpoints de autenticação e redefinição de senha para conter tentativas de força bruta.",
        "Senhas com hash bcrypt (12 rounds) — o texto em claro nunca sai da memória do navegador.",
        "Log de auditoria completo e resistente a adulteração, visível para Super Admins e exportável em CSV.",
        "SSO opcional via Google, Microsoft ou qualquer provedor de identidade SAML 2.0, com provisionamento just-in-time.",
        "Autenticação multifator disponível para qualquer usuário nas configurações pessoais de segurança.",
      ],
    },
    {
      icon: FileCheck2,
      title: "Privacidade dos dados",
      bullets: [
        "Todo funcionário pode exportar a qualquer momento uma cópia completa dos seus dados pessoais.",
        "Pedidos de exclusão são processados em até 30 dias, com janela de graça agendada para evitar perdas acidentais.",
        "A Lenavio nunca vende dados de funcionários para terceiros — ponto final.",
        "Dados de cada cliente são estritamente escopados: toda consulta é filtrada por organização, com verificação imposta no nível da ORM, não por convenção.",
        "Residência regional de dados disponível nos planos enterprise.",
      ],
    },
    {
      icon: Sparkles,
      title: "IA responsável",
      bullets: [
        "Nossos agentes de IA (Atlas, Sentinel, Pilot) rodam na API Claude da Anthropic com tratamento de dados sem retenção.",
        "Nenhum dado de cliente ou funcionário é usado para treinar modelos fundacionais.",
        "Conteúdo gerado por IA aparece como sugestão — um humano revisa e aprova antes de qualquer ação.",
        "Toda decisão de IA é registrada com o prompt, a resposta do modelo e o humano que agiu com base nela.",
        "Funcionários podem optar por não usar fluxos assistidos por IA nas configurações do perfil.",
      ],
    },
  ],
  compliance: [
    {
      label: "LGPD",
      status: "ready" as const,
      blurb:
        "Lei Geral de Proteção de Dados: controles para acesso, correção, portabilidade e exclusão de dados pessoais.",
    },
    {
      label: "GDPR",
      status: "ready" as const,
      blurb:
        "Regulamento europeu de proteção de dados: base legal, direitos do titular, notificação de incidentes e DPA sob demanda.",
    },
    {
      label: "CCPA / CPRA",
      status: "ready" as const,
      blurb:
        "Direitos de privacidade da Califórnia: direito de saber, excluir, corrigir e recusar a venda ou compartilhamento de informações pessoais.",
    },
    {
      label: "SOC 2 Type II",
      status: "roadmap" as const,
      blurb:
        "Auditoria prevista para o fim de 2026. Os controles de segurança já estão mapeados para os Trust Services Criteria.",
    },
  ],
};

// ── Presentational pieces ─────────────────────────────────────────────

function SectionCard({ icon: Icon, title, blurb, bullets }: BulletSection) {
  return (
    <section className="not-prose rounded-xl border border-border bg-surface p-6 md:p-7">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent shrink-0">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold font-heading text-text-primary">
            {title}
          </h2>
          {blurb && (
            <p className="text-sm text-text-muted mt-1">{blurb}</p>
          )}
          <ul className="mt-4 space-y-2 text-[15px] leading-relaxed text-text-primary">
            {bullets.map((b) => (
              <li key={b} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="mt-2 h-1.5 w-1.5 rounded-full bg-accent shrink-0"
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ContactRow({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent shrink-0">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary">{title}</p>
        <div className="text-sm text-text-muted mt-1">{children}</div>
      </div>
    </div>
  );
}

export function TrustPage() {
  const [lang, setLang] = useState<Lang>("en");
  const t = lang === "en" ? EN : PT;

  const securityEmail = (
    <a
      className="text-accent hover:underline"
      href="mailto:security@lenavio.com"
    >
      security@lenavio.com
    </a>
  );
  const privacyEmail = (
    <a
      className="text-accent hover:underline"
      href="mailto:privacy@lenavio.com"
    >
      privacy@lenavio.com
    </a>
  );

  return (
    <LegalLayout
      title={t.title}
      effectiveDate={lang === "en" ? EFFECTIVE_DATE : EFFECTIVE_DATE_PT}
      sibling={t.sibling}
    >
      {/* Language toggle */}
      <div className="flex items-center gap-2 -mt-4 mb-8 not-prose">
        <button
          type="button"
          onClick={() => setLang("en")}
          className={
            "px-3 py-1 rounded-full text-xs font-medium transition-colors " +
            (lang === "en"
              ? "bg-accent text-white"
              : "bg-surface border border-border text-text-muted hover:text-text-primary")
          }
        >
          {t.langToggleEn}
        </button>
        <button
          type="button"
          onClick={() => setLang("pt")}
          className={
            "px-3 py-1 rounded-full text-xs font-medium transition-colors " +
            (lang === "pt"
              ? "bg-accent text-white"
              : "bg-surface border border-border text-text-muted hover:text-text-primary")
          }
        >
          {t.langTogglePt}
        </button>
      </div>

      {/* Hero */}
      <section className="not-prose mb-10">
        <div className="flex items-center gap-3 text-accent">
          <ShieldCheck className="h-6 w-6" aria-hidden="true" />
          <span className="text-sm font-semibold uppercase tracking-wider">
            {t.trustCenter}
          </span>
        </div>
        <p className="mt-4 text-2xl md:text-3xl font-heading text-text-primary leading-snug">
          {t.hero}
        </p>
        <p className="mt-3 text-text-muted max-w-2xl">{t.heroBlurb}</p>
      </section>

      <div className="not-prose space-y-6">
        {t.sections.map((s) => (
          <SectionCard key={s.title} {...s} />
        ))}
      </div>

      {/* Compliance */}
      <section className="not-prose mt-10">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-accent" aria-hidden="true" />
          <h2 className="text-xl font-semibold font-heading text-text-primary">
            {t.complianceTitle}
          </h2>
        </div>
        <p className="text-sm text-text-muted mt-2">{t.complianceBlurb}</p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {t.compliance.map((b) => (
            <div
              key={b.label}
              className="rounded-xl border border-border bg-surface p-4 flex items-start gap-3"
            >
              <span
                className={
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold shrink-0 " +
                  (b.status === "ready"
                    ? "bg-success/10 text-success"
                    : "bg-warning/10 text-warning")
                }
              >
                {b.status === "ready" ? t.readyBadge : t.roadmapBadge}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary">
                  {b.label}
                </p>
                <p className="text-xs text-text-muted mt-1 leading-relaxed">
                  {b.blurb}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Reporting + DPA */}
      <section className="not-prose mt-10">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-accent" aria-hidden="true" />
          <h2 className="text-xl font-semibold font-heading text-text-primary">
            {t.contactsTitle}
          </h2>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <ContactRow icon={Mail} title={t.reportVulnTitle}>
            {t.reportVulnBody(securityEmail)}
          </ContactRow>
          <ContactRow icon={ScrollText} title={t.dpaTitle}>
            {t.dpaBody(privacyEmail)}
          </ContactRow>
        </div>
      </section>
    </LegalLayout>
  );
}
